/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the sae view page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

import { useNavigate, useParams } from 'react-router';

import { Box, Paper } from '@mui/material';

import {
    InvalidSaeForm,
    SaeBreadcrumbs,
    SaeContext,
    SaeHistory,
    SaeInformation,
    SaeInformationFn,
    SaeInvestigatorReviewForm,
    SaeMedicalReviewForm,
    SaeExtensionContext,
    SaeSummary,
    SaeSummaryButtons
} from '@ngt/opms-sae';

import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';

import {
    InstitutionContext, PageLayout, RouteLoading,
} from '@ngt/opms';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../api/dtos';
import useSaeColumns from '../../hooks/sae/useSaeColumns';
import { FunctionComponent, useContext, useEffect } from 'react';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface ISaeViewPageProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */
const ValidSaeView: FunctionComponent<{ canReviewSae: boolean; canMedicalReviewSae: boolean; }>  = ({
    canReviewSae,
    canMedicalReviewSae
}) => {
    const params = useParams();

    const { data: institution } = useContext(InstitutionContext);

    const { data: sae } = useContext(SaeContext);

    const { hasMedicalReview } = useContext(SaeExtensionContext);

    const saeDataColumns = useSaeColumns(null, false) as any;

    return (
        <>
            {
                params?.viewMode === 'review' &&
                !!canReviewSae &&
                <Box sx={{mb: 2}}>
                    <SaeInvestigatorReviewForm />
                </Box>
            }

            {
                params?.viewMode === 'mreview' &&
                !!hasMedicalReview &&
                !!canMedicalReviewSae &&
                <Box sx={{mb: 2}}>
                    <SaeHistory
                        patientStudyNumber={sae?.patient?.studyNumber}
                        saeNumber={sae?.saeNumber}
                        institutionCode={institution?.code}
                        columns={saeDataColumns}
                    />
                    <SaeMedicalReviewForm
                        patientStudyNumber={sae?.patient.studyNumber}
                        saeNumber={sae?.saeNumber}
                    />
                </Box>
            }

            {
                params?.viewMode !== 'review' &&
                params?.viewMode !== 'mreview' &&
                <Box paddingX={2}>
                    <SaeSummaryButtons />
                </Box>
            }

            <SaeSummary
                showMedicalReview={!!hasMedicalReview && !!canMedicalReviewSae}
            />
        </>    
    )
}

const InvalidSaeView: FunctionComponent<{ canAdministerSae: boolean }> = ({
    canAdministerSae
}) => {
    return (
        <>
            <InvalidSaeForm readOnly={!canAdministerSae} />
        </>
    )
}

const SaeViewPage: FunctionComponent<ISaeViewPageProps> = () => {
    const navigate = useNavigate();

    const params = useParams();

    const viewMode = params?.viewMode;

    const { hasMedicalReview } = useContext(SaeExtensionContext);

    const contextInstitution = useContext(InstitutionContext);

    const institution = contextInstitution?.data;

    const {
        permissions: [canViewSae, canReviewSae, canAdministerSae, canMedicalReviewSae],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.SaeView,
            Permission.SaeReview,
            Permission.SaeAdminister,
            Permission.MedicalReviewPerform
        ], 
        undefined, 
        undefined, 
        institution?.id
    );

    const patientCaption: SaeInformationFn = (patient) => (patient as Dtos.Patient)?.initials;
    
    const saeText: SaeInformationFn = (_patient, _institution, sae) => sae ? `${sae?.saeNumber} (${sae?.form.followUpNumber === 0 ? 'Initial' : 'Follow Up No.' + sae?.form.followUpNumber})` : '';
    
    const saeCaption: SaeInformationFn = (_patient, _institution, sae) => getSaeStatus((sae as any as Dtos.Sae<Dtos.ISaeForm, Dtos.IPatient>)?.status) ?? '';

    const { data: sae, loading: saeLoading } = useContext(SaeContext);

    useEffect(() => {
        if (viewMode === 'mreview' && !hasMedicalReview) {
            navigate(`/sae/${institution?.code}/${sae?.patient?.studyNumber}/${sae?.saeNumber}`, { replace: true });
        }
    }, [viewMode, hasMedicalReview, institution, sae, navigate]);

    useEffect(() => {
        if (!permissionsLoading) {
            if (!canViewSae) {
                navigate(`/error/403`, { replace: true });
            }
        }
    }, [canViewSae, permissionsLoading, navigate]);


    if (saeLoading || permissionsLoading) {
        return <RouteLoading />;
    }

    return (
        <PageLayout
            breadcrumbs={<SaeBreadcrumbs institution={institution} />}
            heading="Serious Adverse Event"
        >
            <Paper>
                <Box
                    padding={2}
                >
                    <SaeInformation patientCaption={patientCaption} saeText={saeText} saeCaption={saeCaption} />
                </Box>

                {
                    sae?.status !== Dtos.SaeStatus.Invalid && viewMode !== 'invalid' ?
                        <ValidSaeView canMedicalReviewSae={canMedicalReviewSae} canReviewSae={canReviewSae} /> :
                        <InvalidSaeView canAdministerSae={canAdministerSae} />
                }
            </Paper>
        </PageLayout>
    );
}

export const getSaeStatus = (status?: Dtos.SaeStatus) => {
    switch (status) {
        case Dtos.SaeStatus.Added: return 'New';
        case Dtos.SaeStatus.New: return 'New';
        case Dtos.SaeStatus.AwaitingForReview: return 'Awaiting for review';
        case Dtos.SaeStatus.Approved: return 'Approved';
        case Dtos.SaeStatus.Rejected: return 'Rejected';
        case Dtos.SaeStatus.Invalid: return 'Invalid';
    }

    return;
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default SaeViewPage;